import React, { useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Form, Input, Modal, Typography, message } from 'antd';
import { createQuote, updateQuote } from '@erp/quotes/api/quotes.api';
import type { QuoteCreatePayload, QuoteOut, QuoteUpdatePayload } from '@erp/quotes/types';
import type { ManualDiscountDraft, PosSaleLine } from '../types';
import type { Customer } from '@store/slices/posCustomersSlice.tsx';
import { buildCustomerSnapshot, buildQuoteLines } from '@erp/quotes/utils/quotePayload';
import { useQuoteAccess } from '@erp/quotes/hooks/useQuoteAccess';
import { resolveErrorMessage } from '../utils/checkout';
import { buildIdempotencyKey } from '../utils/idempotency';

interface QuoteSaveModalProps {
  open: boolean;
  quoteId?: number | null;
  customerId?: number | null;
  customer?: Customer | null;
  currencyCode: string;
  lines: PosSaleLine[];
  orderDiscount?: ManualDiscountDraft | null;
  shipping?: { methodId: number; methodName: string; amount: number; currency: string; addressId?: string | null } | null;
  onClose: () => void;
  onSaved?: (quote: QuoteOut) => void;
}

interface QuoteSaveFormValues {
  title?: string;
  notes?: string;
  internal_notes?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  expires_at?: { toISOString?: () => string } | null;
}

const toNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const buildQuotePayload = (
  values: QuoteSaveFormValues,
  lines: PosSaleLine[],
  customerId: number | null | undefined,
  currencyCode: string,
  orderDiscount?: ManualDiscountDraft | null,
  customer?: Customer | null,
  shipping?: QuoteSaveModalProps['shipping'],
): QuoteCreatePayload => {
  const normalizedCurrency = currencyCode || 'ARS';
  const draftItems = lines.map((line) => ({
    itemId: line.item.id,
    item_id: line.item.id,
    name: line.item.name,
    sku: line.item.sku ?? null,
    item_type: line.item.item_type,
    itemType: line.item.item_type,
    quantity: line.quantity,
    unitPrice: toNumber(line.item.price?.value),
    unit_price: toNumber(line.item.price?.value),
    currency_code: line.item.price?.currency ?? normalizedCurrency,
    variantId: line.variantId ?? null,
    variant_id: line.variantId ?? null,
    professionalId: line.professionalId ?? null,
    professional_id: line.professionalId ?? null,
    lineKey: line.lineKey ?? null,
    line_key: line.lineKey ?? null,
    notes: line.notes ?? null,
    manualDiscount: line.manualDiscount ?? null,
    commission: line.commission ?? null,
    flags: line.flags ?? null,
    options: {
      variantId: line.variantId ?? null,
      subscriptionId: line.subscriptionId ?? null,
      useSubscription: Boolean(line.useSubscription),
      subscriptionAvailable: line.subscriptionAvailable ?? null,
      subscriptionUnlimited: Boolean(line.subscriptionUnlimited),
      subscriptionName: line.subscriptionName ?? null,
    },
  }));
  const contactSnapshot =
    values.contact_name?.trim() || values.contact_email?.trim() || values.contact_phone?.trim()
      ? {
          name: values.contact_name?.trim() || null,
          email: values.contact_email?.trim() || null,
          phone: values.contact_phone?.trim() || null,
        }
      : null;

  return {
    customer_id: customerId ?? null,
    customer_snapshot: buildCustomerSnapshot(customer) ?? contactSnapshot,
    title: values.title?.trim() || null,
    source: 'pos',
    currency_code: normalizedCurrency,
    order_discount_snapshot: orderDiscount ? { ...orderDiscount } : null,
    shipping_snapshot: shipping ?? null,
    shipping_total: shipping?.amount ?? 0,
    notes: values.notes?.trim() || null,
    internal_notes: values.internal_notes?.trim() || null,
    expires_at: values.expires_at?.toISOString?.() ?? null,
    idempotency_key: buildIdempotencyKey('pos-quote'),
    metadata: {
      contact: contactSnapshot,
      orderDiscount: orderDiscount ?? null,
      shipping: shipping ?? null,
    },
    pos_draft: {
      customerId: customerId ?? null,
      currencyCode: normalizedCurrency,
      items: draftItems,
      orderDiscount: orderDiscount ?? null,
      shipping: shipping ?? null,
    },
    lines: buildQuoteLines(lines, normalizedCurrency),
  };
};

export const buildQuoteUpdatePayload = (
  values: QuoteSaveFormValues,
  lines: PosSaleLine[],
  customerId: number | null | undefined,
  currencyCode: string,
  orderDiscount?: ManualDiscountDraft | null,
  customer?: Customer | null,
  shipping?: QuoteSaveModalProps['shipping'],
): QuoteUpdatePayload => {
  const payload = buildQuotePayload(values, lines, customerId, currencyCode, orderDiscount, customer, shipping);
  return {
    customer_id: payload.customer_id,
    customer_snapshot: payload.customer_snapshot,
    title: payload.title,
    currency_code: payload.currency_code,
    notes: payload.notes,
    internal_notes: payload.internal_notes,
    expires_at: payload.expires_at,
    pos_draft: payload.pos_draft,
    metadata: payload.metadata,
    order_discount_snapshot: payload.order_discount_snapshot,
    shipping_snapshot: payload.shipping_snapshot,
    shipping_total: payload.shipping_total,
    lines: payload.lines,
  };
};

export const QuoteSaveModal: React.FC<QuoteSaveModalProps> = ({
  open,
  quoteId = null,
  customerId,
  customer,
  currencyCode,
  lines,
  orderDiscount,
  shipping,
  onClose,
  onSaved,
}) => {
  const isEditing = quoteId != null;
  const [form] = Form.useForm<QuoteSaveFormValues>();
  const [isSaving, setSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { canWrite } = useQuoteAccess();

  const lineCount = lines.length;
  const total = useMemo(
    () =>
      lines.reduce(
        (sum, line) => sum + toNumber(line.item.price?.value) * line.quantity,
        0,
      ),
    [lines],
  );

  const handleClose = () => {
    if (!isSaving) {
      form.resetFields();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!canWrite) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      let quote: QuoteOut;
      if (isEditing && quoteId != null) {
        quote = await updateQuote(
          quoteId,
          buildQuoteUpdatePayload(values, lines, customerId, currencyCode, orderDiscount, customer, shipping),
        );
        messageApi.success(`Presupuesto #${quote.id} actualizado.`);
      } else {
        quote = await createQuote(buildQuotePayload(values, lines, customerId, currencyCode, orderDiscount, customer, shipping));
        messageApi.success(`Presupuesto #${quote.id} guardado.`);
      }
      form.resetFields();
      onSaved?.(quote);
      onClose();
    } catch (error) {
      const isValidationError =
        typeof error === 'object' && error !== null && 'errorFields' in error;
      if (!isValidationError) {
        messageApi.error(resolveErrorMessage(error));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEditing ? `Editar presupuesto #${quoteId}` : 'Guardar presupuesto'}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={isSaving}>
          Cancelar
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={() => void handleSubmit()}
          loading={isSaving}
          disabled={!canWrite || lineCount === 0}
        >
          {isEditing ? 'Guardar cambios' : 'Guardar'}
        </Button>,
      ]}
      zIndex={1200}
      destroyOnClose
    >
      {contextHolder}
      <Typography.Paragraph type="secondary">
        {lineCount} lineas. Total estimado {currencyCode} {total.toFixed(2)}.
      </Typography.Paragraph>
      {!customerId ? (
        <Alert
          type="warning"
          showIcon
          message="Se guardara como borrador interno si no asocias un cliente o contacto."
          style={{ marginBottom: 12 }}
        />
      ) : null}
      <Form form={form} layout="vertical">
        <Form.Item name="title" label="Titulo">
          <Input maxLength={160} placeholder="Presupuesto desde POS" />
        </Form.Item>
        {!customerId ? (
          <>
            <Form.Item name="contact_name" label="Contacto">
              <Input maxLength={160} placeholder="Nombre o referencia" />
            </Form.Item>
            <Form.Item name="contact_email" label="Email">
              <Input maxLength={180} type="email" />
            </Form.Item>
            <Form.Item name="contact_phone" label="Telefono">
              <Input maxLength={80} />
            </Form.Item>
          </>
        ) : null}
        <Form.Item name="expires_at" label="Vencimiento">
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="notes" label="Notas para el cliente">
          <Input.TextArea rows={3} maxLength={1000} />
        </Form.Item>
        <Form.Item name="internal_notes" label="Nota interna">
          <Input.TextArea rows={2} maxLength={1000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
